// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EmptyState } from '@/components/empty-state';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import { ArrowOutwardRounded } from '@/constants/hardfin-standard-icons';
import { Sort } from '@/constants/sort';
import {
  TimeRecordFilter,
  TimeRecordSortProperty,
} from '@/costs/api/get-time-records';
import { TimeRecordTable } from '@/costs/components/time-record-table';
import { useTimeRecords } from '@/costs/hooks/use-time-records';
import { Box, LinearProgress, Paper, Stack, Typography } from '@mui/material';

interface Props {
  sort: Sort<TimeRecordSortProperty>;
  filter?: TimeRecordFilter;
  onSort: (property: TimeRecordSortProperty) => void;
  onPageChange: (page: number) => void;
}

/**
 * A view component that displays a table of time records with loading states and pagination.
 */
export function TimeRecordTableView(props: Props) {
  const { timeRecords, totalCount, isFetching } = useTimeRecords({
    filter: props.filter,
  });

  const downloadURL = new URL(
    '/v1/export/time-records',
    import.meta.env.VITE_API_URL
  );
  const pageNumber = props.filter?.pageNumber ?? 1;
  const pageSize = props.filter?.pageSize ?? 50;

  if (timeRecords.length === 0) {
    return (
      <EmptyState title="No time records found">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography color="text.secondary">
            Start tracking time by adding your first time record
          </Typography>
          <ArrowOutwardRounded fontSize="small" color="action" />
        </Stack>
      </EmptyState>
    );
  }

  return (
    <>
      <Paper variant="outlined" component={Box} overflow="hidden">
        {isFetching && <LinearProgress />}
        <TimeRecordTable
          sort={props.sort}
          onSort={props.onSort}
          timeRecords={timeRecords}
        />
      </Paper>
      <TablePaginationFooter
        pagination={{
          page: pageNumber,
          pageSize,
          onPageChange: props.onPageChange,
        }}
        totalCount={totalCount}
        entityOptions={{
          singular: 'time record',
          plural: 'time records',
          includeCount: false,
        }}
        downloadURL={downloadURL}
        downloadLabel="Export time data"
      />
    </>
  );
}
